import React from 'react';
import PreferencesSection from './PreferencesSection';
import PropTypes from 'prop-types';
import { getWord } from 'utils/tenant';
import contactCss from './ContactInformation.scss';
import css from './PreferencesSection.scss';
import Phone from './Phone';
import AlternateEmail from './AlternateEmail';
import CellPhone from './CellPhone';
import Icon from 'SF/MaterialIcon';
import classnames from 'classnames';
import TimeZone from './TimeZone';
import { isEmptyString } from 'utils/text';
import InstitutionEmail from './InstitutionEmail';
import Pronouns from './Pronouns';

/**
 * @param {object} props - see propTypes
 * @returns {JSX.Element} a user's contact information preferences to be displayed and edited from their profile.
 */
const ContactInformation = (props) => {
    const getDescription = () => props.isStaff
        ? 'View and update contact information.'
        : 'Some of your information is automatically updated by your institution and cannot be edited.';

    return (
        <PreferencesSection className={classnames(contactCss['contact-options'], { [contactCss.ie]: props.isIE })}
                            headerText="Contact Information"
                            descriptionText={getDescription()}
                            isStaff={props.isStaff}>
            <Pronouns isEditable={props.allowEditPronouns}
                      isIE={props.isIE}
                      isStaff={props.isStaff}
                      pronouns={props.pronouns}
                      setValue={(newUserProfile) => props.setValue({userProfile: newUserProfile})}
                      showPronouns={props.showPronouns}
                      staffHasStudentRole={props.staffHasStudentRole} />
            <div id="username-section">
                <label id="username">Username</label>
                <div className={css['fake-input'] + ' disabled'}>
                    <span aria-labelledby="username"
                          className={classnames(css['overflow-fade'], { [css.ie]: props.isIE })}>
                        {props.userName}
                    </span>
                    <Icon icon="lock" aria-hidden={true} />
                </div>
            </div>
            <InstitutionEmail allowPreferredEmail={props.allowPreferredEmail}
                              correspondenceTo={props.correspondenceTo}
                              goToNotificationsTab={props.goToNotificationsTab}
                              isIE={props.isIE}
                              isStaff={props.isStaff}
                              schoolEmail={props.schoolEmail}
                              setValue={(newUserProfile) => props.setValue({userProfile: newUserProfile})} />
            <AlternateEmail allowPreferredEmail={props.allowPreferredEmail}
                            correspondenceTo={props.correspondenceTo}
                            goToNotificationsTab={props.goToNotificationsTab}
                            isStaff={props.isStaff}
                            mobileEmailInfoLink={props.mobileEmailInfoLink}
                            preferredEmail={props.preferredEmail}
                            setValue={(newUserProfile) => props.setValue({userProfile: newUserProfile})}
                            showMobileEmailInfo={props.showMobileEmailInfo}
                            showNotificationsLinks={props.showNotificationsLinks} />
            <div id="default-phone-section">
                <label id="defaultPhone">{getWord('defaultPhone')}</label>
                <Phone labelledById="defaultPhone"
                       onChange={updatedPhone => props.setValue({ userProfile: {phone: isEmptyString(updatedPhone) ? '' : updatedPhone}})}
                       value={props.phone} />
            </div>
            <CellPhone cellPhone={props.cellPhone}
                       goToNotificationsTab={props.goToNotificationsTab}
                       isStaff={props.isStaff}
                       setValue={props.setValue}
                       showNotificationsLinks={props.showNotificationsLinks}
                       supportedCountries={props.supportedCountries}
                       textMessagingEnabled={props.textMessagingEnabled}
                       textNotificationEnabledForUser={props.textNotificationEnabledForUser} />
            <div id="video-phone-section">
                <label id="videoPhone">{getWord('videoPhone')}</label>
                <input type="text"
                       aria-labelledby="videoPhone"
                       onChange={(e) => props.setValue({ userProfile: {videoPhone: e.target.value}})}
                       value={props.videoPhone} />
            </div>
            <TimeZone setValue={(newUserProfile) => props.setValue({userProfile: newUserProfile})}
                      timeZone={props.timeZone} />
        </PreferencesSection>
    );
};

ContactInformation.defaultProps = {
    allowEditPronouns: false,
    isStaff: false,
    showPronouns: false,
    staffHasStudentRole: false
};

ContactInformation.propTypes = {
    allowEditPronouns: PropTypes.bool,
    allowPreferredEmail: PropTypes.bool,
    cellPhone: PropTypes.string,
    correspondenceTo: PropTypes.oneOf(['BOTH_MAIL', 'SCHOOL_MAIL', 'PREFERRED_MAIL', '']),
    goToNotificationsTab: PropTypes.func,
    isIE: PropTypes.bool,
    isStaff: PropTypes.bool,
    mobileEmailInfoLink: PropTypes.string,
    phone: PropTypes.string,
    preferredEmail: PropTypes.string,
    pronouns: PropTypes.string,
    setValue: PropTypes.func.isRequired,
    schoolEmail: PropTypes.string,
    showMobileEmailInfo: PropTypes.bool,
    showNotificationsLinks: PropTypes.bool,
    showPronouns: PropTypes.bool,
    staffHasStudentRole: PropTypes.bool,
    supportedCountries: PropTypes.string,
    textMessagingEnabled: PropTypes.bool,
    textNotificationEnabledForUser: PropTypes.bool,
    timeZone: PropTypes.string,
    userName: PropTypes.string,
    videoPhone: PropTypes.string
};

export default ContactInformation;
